import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  getPlans,
  getPlan,
  createPlan,
  deletePlan,
  checkoffPlanDay,
  getTrainingStats,
  type PlanMeta,
  type PlanDetail,
  type TrainingStats,
  type PlanDistance,
  type CreatePlanBody,
  type PlanBlobDay,
  type PlanBlobWeek,
  type PlanWorkout,
} from '../api.js';
import PlanCalendar from '../components/PlanCalendar.js';
import Sparkline from '../components/Sparkline.js';
import { parseHMS, fmtHMS, fmtPacePref, fmtDistanceKm } from '../lib/trainingFormat.js';
import { localYmd } from '../lib/foodPage.js';
import { Button, Card, EmptyState, ErrorState, Group, IconButton, Page, Ring, Row, Segmented, Sheet } from '../ui/index.js';
import { useUnitPref } from '../hooks/useUnitPref.js';
import type { UnitPref } from '../lib/units.js';

const DISTANCES: { id: PlanDistance; label: string }[] = [
  { id: '5k', label: '5K' },
  { id: '10k', label: '10K' },
  { id: 'half', label: 'Half' },
  { id: 'full', label: 'Marathon' },
];

export default function Training(): JSX.Element {
  const unitPref = useUnitPref();
  const [plans, setPlans] = useState<PlanMeta[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    const [list, s] = await Promise.all([getPlans(), getTrainingStats()]);
    setPlans(list);
    setStats(s);
    return list;
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const d = await getPlan(id);
    setDetail(d);
    setActiveId(id);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadList()
      .then((list) => {
        if (!alive) return;
        if (list.length > 0) return loadDetail(list[0]!.id);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : 'Failed to load training.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [loadList, loadDetail]);

  async function onCreated(id: string): Promise<void> {
    setCreating(false);
    await loadList();
    await loadDetail(id);
  }

  async function onToggle(planWorkoutId: string, completed: boolean): Promise<void> {
    if (!activeId) return;
    setBusyId(planWorkoutId);
    try {
      await checkoffPlanDay(activeId, planWorkoutId, completed);
      await loadDetail(activeId);
      setStats(await getTrainingStats());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check-off failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!confirm('Delete this plan? This cannot be undone.')) return;
    await deletePlan(id);
    setDetail(null);
    setActiveId(null);
    const list = await loadList();
    if (list.length > 0) await loadDetail(list[0]!.id);
  }

  const summary = detail
    ? `${detail.plan.meta.distanceLabel} · ${detail.weeks} weeks · ${detail.daysPerWeek} days a week`
    : undefined;

  return (
    <Page
      title="Training"
      sub={summary}
      actions={<IconButton icon="plus" label="New plan" onClick={() => setCreating(true)} />}
    >
      {error && <ErrorState message={error} />}

      {creating && <CreatePlanForm onCreated={onCreated} onClose={() => setCreating(false)} />}

      {plans.length > 1 && (
        <div className="hb-seg">
          <Segmented
            label="Plan"
            value={activeId ?? ''}
            options={plans.map((p) => ({ value: p.id, label: p.name ?? p.distance.toUpperCase() }))}
            onChange={(id) => void loadDetail(id)}
          />
        </div>
      )}

      {loading && !detail ? (
        <div className="hb-chart tr-skel" aria-hidden="true" />
      ) : !detail ? (
        <Card className="tr-empty">
          <EmptyState
            icon="activity"
            title="No plan yet"
            hint="Give it your current time and a goal; it builds a periodized schedule you tick off day by day."
            action={
              <Button icon="plus" onClick={() => setCreating(true)}>
                Create a plan
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <NextRun detail={detail} busyId={busyId} onToggle={onToggle} unitPref={unitPref} />
          <section className="ui-groupwrap" aria-label="Schedule">
            <div className="ui-group__head">
              <h2 className="ui-group__title">Schedule</h2>
            </div>
            <div className="hb-chart">
              <PlanCalendar
                weeks={detail.plan.weeks}
                planWorkouts={detail.planWorkouts}
                onToggle={onToggle}
                busyId={busyId}
                unitPref={unitPref}
              />
            </div>
          </section>
        </>
      )}

      <StatsPanel stats={stats} unitPref={unitPref} />

      {detail && <PlanFacts detail={detail} onDelete={() => onDelete(detail.id)} />}
    </Page>
  );
}

/** The one thing to do: the next un-done run (today first), with Mark done. */
function NextRun({
  detail,
  busyId,
  onToggle,
  unitPref,
}: {
  detail: PlanDetail;
  busyId: string | null;
  onToggle: (planWorkoutId: string, completed: boolean) => void;
  unitPref: UnitPref;
}): JSX.Element | null {
  const today = localYmd(new Date());
  const byDate = new Map(detail.planWorkouts.map((w) => [w.date, w]));
  let next: { day: PlanBlobDay; week: PlanBlobWeek; row: PlanWorkout } | null = null;
  for (const week of detail.plan.weeks) {
    for (const day of week.days) {
      const row = byDate.get(day.date);
      if (day.isRun && row && !row.completed && day.date >= today) {
        next = { day, week, row };
        break;
      }
    }
    if (next) break;
  }
  const done = detail.planWorkouts.filter((w) => w.completed).length;
  const total = detail.planWorkouts.length;
  if (!next) {
    return (
      <Card eyebrow="Plan complete" title={`${done} of ${total} runs done`}>
        <p className="hb-hint">Nothing left on this plan. Start a new one with +.</p>
      </Card>
    );
  }
  const { day, week, row } = next;
  const isToday = day.date === today;
  const when = isToday
    ? 'Today'
    : new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  const pace = day.targetPaceSecPerKm ?? row.targetPaceSecPerKm ?? null;
  return (
    <Card
      className="tr-next"
      accent={`var(--phase-${week.phase}, var(--accent))`}
      eyebrow={
        <>
          <span>{isToday ? 'Today' : 'Next run'}</span>
          <span className="tr-next__wk">
            Week {week.weekNumber} of {detail.weeks} · {week.phaseName || week.phase}
          </span>
        </>
      }
      title={day.type}
    >
      <p className="tr-next__meta">
        {!isToday && <span>{when}</span>}
        {day.distanceKm != null && <span className="num">{fmtDistanceKm(day.distanceKm, unitPref)}</span>}
        {pace != null && pace > 0 && <span className="num">{fmtPacePref(pace, unitPref)}</span>}
      </p>
      {day.description && <p className="tr-next__desc">{day.description.split('\n')[0]}</p>}
      {isToday && (
        <div className="ui-card__actions">
          <Button icon="check" size="lg" disabled={busyId === row.id} onClick={() => onToggle(row.id, true)}>
            Mark done
          </Button>
        </div>
      )}
    </Card>
  );
}

function PlanFacts({ detail, onDelete }: { detail: PlanDetail; onDelete: () => void }): JSX.Element {
  const m = detail.plan.meta;
  const done = detail.planWorkouts.filter((w) => w.completed).length;
  const total = detail.planWorkouts.length;
  return (
    <Group header="This plan">
      <Row title="Goal pace" detail={<span className="num">{m.goalPaceStr}/mi</span>} />
      <Row title="Fitness (VDOT)" detail={<span className="num">{m.vdot} → {m.goalVdot}</span>} />
      <Row title="Outlook" detail={m.feasibility} />
      <Row title="Runs done" detail={<span className="num">{done} of {total}</span>} />
      <Row title="Delete plan" tone="bad" onClick={onDelete} chevron={false} />
    </Group>
  );
}

function StatsPanel({
  stats,
  unitPref,
}: {
  stats: TrainingStats | null;
  unitPref: UnitPref;
}): JSX.Element | null {
  if (!stats) return null;
  const trendPoints = stats.vdotTrend.map((p) => ({ t: p.date, v: p.vdot }));
  const first = stats.vdotTrend[0];
  const last = stats.vdotTrend[stats.vdotTrend.length - 1];
  return (
    <>
      {(stats.adherence || trendPoints.length >= 2) && (
        <div className="hb-grid">
          {stats.adherence && (
            <div className="hb-tile" style={{ '--tile-accent': 'var(--ring-move)' } as CSSProperties}>
              <div className="hb-tile__head">
                <span className="hb-tile__label">On plan</span>
              </div>
              <div className="tr-adh">
                <span className="hb-tile__value">{stats.adherence.percent}%</span>
                <Ring
                  value={stats.adherence.percent}
                  max={100}
                  color="var(--ring-move)"
                  size={40}
                  stroke={6}
                  label={`${stats.adherence.percent}% of runs done`}
                />
              </div>
              <div className="hb-tile__foot">
                <span className="hb-tile__note">
                  {stats.adherence.completed} of {stats.adherence.pastTotal} runs
                </span>
              </div>
            </div>
          )}
          {trendPoints.length >= 2 && first && last && (
            <div className="hb-tile" style={{ '--tile-accent': 'var(--accent)' } as CSSProperties}>
              <div className="hb-tile__head">
                <span className="hb-tile__label">VDOT</span>
              </div>
              <div className="hb-tile__readout">
                <span className="hb-tile__value">{last.vdot}</span>
                <span className="hb-tile__unit">from {first.vdot}</span>
              </div>
              <div className="tr-spark">
                <Sparkline points={trendPoints} color="var(--accent)" width={200} height={28} />
              </div>
            </div>
          )}
        </div>
      )}

      {stats.prs.length > 0 && (
        <Group header="Personal records">
          {stats.prs.map((pr) => (
            <Row
              key={pr.distance}
              title={pr.label}
              sub={
                <span className="num">
                  {fmtPacePref(pr.pacePerKm, unitPref)} · VDOT {pr.vdot}
                </span>
              }
              detail={<span className="tr-pr num">{fmtHMS(pr.timeSec)}</span>}
            />
          ))}
        </Group>
      )}
    </>
  );
}

// --- create form -----------------------------------------------------------

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function CreatePlanForm({ onCreated, onClose }: { onCreated: (id: string) => void; onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [distance, setDistance] = useState<PlanDistance>('half');
  const [pr, setPr] = useState('2:13:00');
  const [goal, setGoal] = useState('1:59:59');
  const [weeks, setWeeks] = useState('12');
  const [daysPerWeek, setDaysPerWeek] = useState('5');
  const [startDate, setStartDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const prSec = useMemo(() => parseHMS(pr), [pr]);
  const goalSec = useMemo(() => parseHMS(goal), [goal]);

  async function submit(): Promise<void> {
    setErr(null);
    if (prSec === null || prSec < 60) return setErr('Enter a valid current time (H:MM:SS).');
    if (goalSec === null || goalSec < 60) return setErr('Enter a valid goal time (H:MM:SS).');
    const w = Number(weeks);
    const d = Number(daysPerWeek);
    if (!Number.isInteger(w) || w < 4 || w > 24) return setErr('Weeks must be 4–24.');
    if (!Number.isInteger(d) || d < 3 || d > 6) return setErr('Days per week must be 3–6.');

    const body: CreatePlanBody = {
      distance,
      prSeconds: prSec,
      goalSeconds: goalSec,
      weeks: w,
      daysPerWeek: d,
      startDate,
    };
    if (name.trim()) body.name = name.trim();

    setBusy(true);
    try {
      const created = await createPlan(body);
      onCreated(created.id);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed to create plan.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="New plan" onClose={onClose} action={{ label: busy ? 'Building…' : 'Create', onClick: () => void submit(), disabled: busy }}>
      <form
        className="hb-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="hb-field">
          <span>Distance</span>
          <select value={distance} onChange={(e) => setDistance(e.target.value as PlanDistance)}>
            {DISTANCES.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <div className="hb-form__row">
          <label className="hb-field">
            <span>Current time</span>
            <input value={pr} onChange={(e) => setPr(e.target.value)} inputMode="numeric" placeholder="0:27:30" />
          </label>
          <label className="hb-field">
            <span>Goal time</span>
            <input value={goal} onChange={(e) => setGoal(e.target.value)} inputMode="numeric" placeholder="0:24:00" />
          </label>
        </div>
        <p className="hb-hint">
          H:MM:SS — reads as {prSec !== null ? fmtHMS(prSec) : '—'} now, {goalSec !== null ? fmtHMS(goalSec) : '—'} goal.
        </p>

        <div className="hb-form__row">
          <label className="hb-field">
            <span>Weeks (4–24)</span>
            <input value={weeks} onChange={(e) => setWeeks(e.target.value)} inputMode="numeric" />
          </label>
          <label className="hb-field">
            <span>Days a week (3–6)</span>
            <input value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)} inputMode="numeric" />
          </label>
        </div>

        <label className="hb-field">
          <span>Start date</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label className="hb-field">
          <span>Name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fall half" />
        </label>

        {err && (
          <p className="hb-form__error" role="alert">
            {err}
          </p>
        )}

        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? 'Building plan…' : 'Create plan'}
        </Button>
      </form>
    </Sheet>
  );
}
